import { t } from "@lingui/macro"
import { useMemo, useCallback } from "react"
import { Box, Typography, DialogContent } from "@mui/material"

import UserInputHistorySection from "./sections/UserInputHistorySection"
import ReformulateNodeSection from "./sections/ReformulateNodeSection"
import ResponseSection from "./sections/ResponseSection"
import AnalysisSection from "./sections/AnalysisSection"
import ConsideredSection from "./sections/ConsideredSection"
import MessageTypeSection from "./sections/MessageTypeSection"
import PromptsSection from "./sections/PromptsSection"

import HeaderSection from "src/components/ChatMessageJourney/sections/HeaderSection"
import { IChatMessage } from "src/types/chat-message/chatMessage"
import {
    ContentBox,
    StyledDialog,
} from "src/components/ChatMessageJourney/styled"
import { IChatMessageJourneyProps } from "src/components/ChatMessageJourney/types"
import { chatbot_MessageType } from "src/api"
import { formatDuration } from "src/helpers/duration"

export const ChatMessageJourney = ({
    isOpenID,
    setIsOpenID,
    messages,
    handleSourceClick,
}: IChatMessageJourneyProps) => {
    const currentMessage: IChatMessage | undefined = useMemo(
        () => messages.find((message) => message.messageId === isOpenID),
        [messages, isOpenID],
    )

    const handleClose = useCallback(() => {
        setIsOpenID("")
    }, [setIsOpenID])

    const hasAnalysisData = useMemo(() => {
        try {
            const analysisData = JSON.parse(currentMessage?.sourceEval ?? "")
            return Array.isArray(analysisData) && analysisData.length > 0
        } catch (error) {
            return false
        }
    }, [currentMessage])

    const hasConsideredSources = useMemo(() => {
        return Boolean(
            currentMessage?.nodeInfo?.find(
                (node) => node.node_name === "GetDataNode",
            )?.state?.sourcesConsidered?.length,
        )
    }, [currentMessage])

    const hasSourcesAndToolsContent = hasAnalysisData || hasConsideredSources

    const totalMessageRuntimeMs = useMemo(() => {
        if (currentMessage?.messageDurationMs != null) {
            return currentMessage.messageDurationMs
        }

        if (
            currentMessage?.nodeInfo == null ||
            currentMessage.nodeInfo.length === 0
        ) {
            return null
        }

        const totalNodes = currentMessage.nodeInfo.reduce((acc, node) => {
            return acc + (node.duration_ms ?? 0)
        }, 0)
        return totalNodes > 0 ? totalNodes : null
    }, [currentMessage])

    if (currentMessage === undefined) {
        return null
    }

    return (
        <Box display="flex" justifyContent="flex-end" mt={2}>
            <StyledDialog
                open={Boolean(isOpenID)}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <HeaderSection setIsOpenID={setIsOpenID} />
                <DialogContent sx={{ pt: 0, pl: 2, pr: 2, pb: 1 }}>
                    <ContentBox>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            {t`chat-message-journey.message`}
                        </Typography>
                        {totalMessageRuntimeMs != null && (
                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block" }}
                                >
                                    {t`chat-message-journey.duration.total-message`}
                                    : {formatDuration(totalMessageRuntimeMs)}
                                </Typography>
                            </Box>
                        )}
                        <UserInputHistorySection messages={messages} />
                        <ReformulateNodeSection message={currentMessage} />
                        {currentMessage.messageType !== undefined && (
                            <MessageTypeSection
                                messageType={
                                    currentMessage.messageType as chatbot_MessageType
                                }
                            />
                        )}
                        <ResponseSection message={currentMessage} />
                        <PromptsSection
                            message={currentMessage}
                            _handleSourceClick={handleSourceClick}
                        />
                    </ContentBox>
                    {hasSourcesAndToolsContent && (
                        <ContentBox>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ mb: 2 }}
                            >
                                {t`chat-message-journey.sources-tools`}
                            </Typography>
                            {hasAnalysisData && (
                                <AnalysisSection
                                    message={currentMessage}
                                    handleSourceClick={handleSourceClick}
                                />
                            )}
                            {hasConsideredSources && (
                                <ConsideredSection
                                    message={currentMessage}
                                    handleSourceClick={handleSourceClick}
                                />
                            )}
                        </ContentBox>
                    )}
                </DialogContent>
            </StyledDialog>
        </Box>
    )
}
